
import { Box, Link, List, ListItem, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { DateTime } from 'luxon';
import { FC, useEffect, useMemo, useState } from 'react';

import Image from '../components/common/Image';

// -- Home page images
import BGImage from '../assets/images/feral-scan/AusOutback-crop.jpg';
import BGImage01 from '../assets/images/feral-scan/background/Background-01.jpg';
import BGImage02 from '../assets/images/feral-scan/background/Background-02.jpg';
import BGImage03 from '../assets/images/feral-scan/background/Background-03.jpg';
import BGImage04 from '../assets/images/feral-scan/background/Background-04.jpg';
import BGImage05 from '../assets/images/feral-scan/background/Background-05.jpg';
import BGImage06 from '../assets/images/feral-scan/background/Background-06.jpg';
import BGImage07 from '../assets/images/feral-scan/background/Background-07.jpg';
import BGImage08 from '../assets/images/feral-scan/background/Background-08.jpg';
import BGImage09 from '../assets/images/feral-scan/background/Background-09.jpg';
import BGImage10 from '../assets/images/feral-scan/background/Background-10.jpg';
import BGImage11 from '../assets/images/feral-scan/background/Background-11.jpg';
import FeralScanLogo from '../assets/images/feral-scan/feralscan-logo-bright-crop.png';
import HomePageButton from '../components/buttons/HomePageButton';

// -- Sponsors
import Banksia from '../assets/images/feral-scan/sponsors/Banksia.small.jpg';
import DepartmentOfPrimaryIndustries from '../assets/images/feral-scan/sponsors/IandINSW.png';
import AusGov from '../assets/images/feral-scan/sponsors/daff.jpg';
import LocalLandServices from '../assets/images/feral-scan/sponsors/localland.small.png';
import AWI from '../assets/images/feral-scan/sponsors/logo-awi.png';
import InvasiveSpeciesSolutions from '../assets/images/feral-scan/sponsors/logo-ciss.svg';
import PetSmart from '../assets/images/feral-scan/sponsors/pestsmart-logo.png';

// -- Page elements
import FacebookIcon from '../assets/images/feral-scan/page-elements/facebook_header.png';
import SubscribeIcon from '../assets/images/feral-scan/page-elements/icon_subscribe_white.png';
import TwitterIcon from '../assets/images/feral-scan/page-elements/twitter_header.png';
import YoutubeIcon from '../assets/images/feral-scan/page-elements/youtube_header.png';

// -- Button logos
import { useSearchParams } from 'react-router-dom';
import DeerIcon from '../assets/images/feral-scan/icons/home/Deer.png';
import DeerHoverIcon from '../assets/images/feral-scan/icons/home/DeerHover.png';
import FeralCatIcon from '../assets/images/feral-scan/icons/home/FeralCat.png';
import FeralCatHoverIcon from '../assets/images/feral-scan/icons/home/FeralCatHover.png';
import FeralFishIcon from '../assets/images/feral-scan/icons/home/Fish.png';
import FeralFishHoverIcon from '../assets/images/feral-scan/icons/home/FishHover.png';
import FoxIcon from '../assets/images/feral-scan/icons/home/Fox.png';
import FoxHoverIcon from '../assets/images/feral-scan/icons/home/FoxHover.png';
import FeralGoatIcon from '../assets/images/feral-scan/icons/home/Goat.png';
import FeralGoatHoverIcon from '../assets/images/feral-scan/icons/home/GoatHover.png';
import MouseIcon from '../assets/images/feral-scan/icons/home/Mouse.png';
import MouseHoverIcon from '../assets/images/feral-scan/icons/home/MouseHover.png';
import MynaIcon from '../assets/images/feral-scan/icons/home/Myna.png';
import MynaHoverIcon from '../assets/images/feral-scan/icons/home/MynaHover.png';
import NewPestsIcon from '../assets/images/feral-scan/icons/home/NewPests.png';
import NewPestsHoverIcon from '../assets/images/feral-scan/icons/home/NewPestsHover.png';
import OtherIcon from '../assets/images/feral-scan/icons/home/OtherPests.png';
import OtherHoverIcon from '../assets/images/feral-scan/icons/home/OtherPestsHover.png';
import FeralPigIcon from '../assets/images/feral-scan/icons/home/Pig.png';
import FeralPigHoverIcon from '../assets/images/feral-scan/icons/home/PigHover.png';
import RabbitIcon from '../assets/images/feral-scan/icons/home/Rabbit.png';
import RabbitHoverIcon from '../assets/images/feral-scan/icons/home/RabbitHover.png';
import StarlingIcon from '../assets/images/feral-scan/icons/home/Starling.png';
import StarlingHoverIcon from '../assets/images/feral-scan/icons/home/StarlingHover.png';
import ToadIcon from '../assets/images/feral-scan/icons/home/Toad.png';
import ToadHoverIcon from '../assets/images/feral-scan/icons/home/ToadHover.png';
import WildDogIcon from '../assets/images/feral-scan/icons/home/WildDog.png';
import WildDogHoverIcon from '../assets/images/feral-scan/icons/home/WildDogHover.png';
import CommonBanner from '../components/banners/CommonBanner';
import Footer from '../components/common/Footer';
import Hr from '../components/common/HorizontalRule';
import { SpeciesTypes } from '../constants/species/Species';
import useWindowDimensions from '../hooks/meta/useWindowDimensions';

interface BackgroundImageProps {
    bgImage: string;
}

interface BackgroundImageBlurProps {
    bgImageBlur: string;
}

const BackgroundImage = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgImage',
})<BackgroundImageProps>(({ bgImage }) => ({
    display: 'block',
    position: 'absolute',
    top: '20px',
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: '900px',
    backgroundImage: `url("${bgImage}")`,
    backgroundPosition: 'center top',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    zIndex: 1,

    '@media (min-width:800px)': {
        top: '0px',
    },
}));

const BackgroundImageBlurTop = styled(Box)(() => ({
    position: 'absolute',
    width: '100%',
    height: '30%',
    top: '-20px',
    left: 0,
    backgroundImage: 'linear-gradient(to top, rgba(255, 255, 255, 0) 0%,#1D2F48 90%)',
    zIndex: 3,

    "@media (min-width: 800px)": {
        top: "0px"
    }
}));

const BackgroundImageBlurBottom = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgImageBlur',
})<BackgroundImageBlurProps>(({ bgImageBlur }) => ({
    position: 'absolute',
    width: '100%',
    height: '75%',
    bottom: 0,
    left: 0,
    backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ${bgImageBlur} 85%)`,
    zIndex: 3,
}));

const HomePageHeaderContainer = styled(Box)(() => ({
    display: 'block',
    backgroundColor: '#E5763E',
    padding: '12px'
}));

const HomePageHeader = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 auto',
    '@media (max-width: 400px)': {
        flexDirection: 'column'
    },
    '@media (min-width: 800px)': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: 0,
        borderBottom: 'none',
        height: '150px'
    },
    '@media (min-width: 1300px)': {
        height: '150px',
        width: '1240px',
        margin: '0 auto',
    },
}));

const HomePageHeaderRightWrapper = styled(Box)(() => ({
    position: 'relative',
    textAlign: 'right',
    zIndex: 8,

    "@media (max-width: 800px)": {
        textAlign: 'center'
    }
}));

const HomePageTitle = styled(Box)(() => ({
    padding: '8px',
    margin: '15px 0',
    position: 'relative',
    zIndex: 8
}));

const HomePageTitleWrapper = styled(Box)(() => ({
    zIndex: 9,
    display: 'block',
    position: 'relative',
    padding: '15px',
    paddingTop: '80px',
    color: 'white',

    "@media (min-width:992px)": {
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'auto'
    },
    "@media (min-width:1300px)": {
        width: '1030px',
        height: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'auto'
    },
}));

const MobileHomePageTitleWrapper = styled(Box)(() => ({
    zIndex: 9,
    display: 'block',
    position: 'relative',
    paddingLeft: '10px',
    paddingRight: '10px',
    color: 'white',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflow: 'auto'
}));

const HomePageBanner = styled(Box)(() => ({
    padding: '0',
    margin: '15px 0',
    zIndex: 8
}));

const HomePageBannerWrapper = styled(Box)(() => ({
    display: 'none',
    margin: '0 auto',
    position: 'relative',
    zIndex: 8,
    width: '0px',

    "@media (min-width:700px)": {
        display: 'block',
        width: '700px',
    }
}))

const HomePageMobileBannerWrapper = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '15px 0',
    padding: '10px',
    position: 'relative',
    zIndex: 8,
    width: '100%',
    backgroundColor: "#E5763E",
    color: 'white',
    fontWeight: '700',
    fontSize: '1.5rem',


    "@media (min-width:700px)": {
        display: 'none',
        width: '0px',
    }
}))

const HomePageButtons = styled(Box)(() => ({
    padding: '10px 0',
    position: 'relative',
    zIndex: 8
}));

const HomePageButtonsWrapper = styled(Box)(() => ({
    '@media (min-width:400px)': {
        paddingLeft: '22px',
        paddingRight: '22px'
    },
    '@media (min-width:800px)': {
        width: '700px',
        height: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'auto'
    },
    '@media (min-width:1300px)': {
        width: '1080px',
        height: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'auto'
    },
    boxSizing: 'content-box'
}));

const HomePageButtonsTable = styled(Box)(() => ({    
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    zIndex: 5,

    '@media (min-width: 500px)': {
        paddingTop: '8px'
    },
    '@media (min-width: 800px)': {
        padding: '24px 0'
    }
}));

const HomePageFooter = styled(Box)(() => ({
    position: 'relative',
    backgroundColor: 'white',
    padding: '0 10px',
    zIndex: 8
}));

const HomePageFooterSponsors = styled(Box)(() => ({
    position: 'relative',
    backgroundColor: 'white',
    margin: 0,
    listStyleType: 'none',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    display: 'flex',
    
    "@media (min-width: 1300px)": {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    }
}));

const HomePageFooterSponsorsWrapper = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',

    "@media (min-width: 1300px)": {
        alignItems: 'center'
    }
}));

const HomePageFooterSponsor = styled(Box)(() => ({
    height: '60%',
    padding: '12px 24px',
    minWidth: '200px',
    width: "auto",

    "img": {
        maxWidth: '100%',
        display: 'inline',
        height: '100%',
        maxHeight: '60px'
    },

    "@media (min-width: 600px)": {
        minWidth: '200px',
        width: 'auto'
    }
}));

const HomePageFooterIcon = styled(Image)(() => ({
    opacity: '0.5',
    filter: 'brightness(30%)',
    maxWidth: '20px',
    minWidth: '20px',
    display: 'inline',
    maxHeight: '20px',
    minHeight: '20px',

    "&:hover": {
        opacity: "1",
        cursor: 'pointer'
    }
}));

const StyledHr = styled(Hr)(() => ({
    visibility: 'visible',

    "@media (min-width:800px)": {
        visibility: 'hidden'
    }
}));

const StyledListItem = styled(ListItem)(() => ({
    margin: 0,
    padding: 0,
    position: 'relative',
    listStyleType: "none",
    fontSize: '0.8rem',
    justifyContent: 'center',
    textAlign: 'center',

    "@media (min-width: 800px)": {
        position: 'relative',
        width: '45%',
        listStyleType: 'none',
        fontSize: '0.8rem',
        color: '#333',
        justifyContent: 'left',
        textAlign: 'left',
    }
}));

const StyledHomePageLink = styled(Link)(() => ({
    textDecoration: "none",
    marginBottom: '5px',
    color: "#999",
    fontWeight: 700,

    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
        color: "#ff6600",
    },
    
    "& p": {
        color: "#999"
    },

    "&:hover p": {
        color: "#ff6600",
    }
}));

const images = [
    BGImage,
    BGImage01,
    BGImage02,
    BGImage03,
    BGImage04,
    BGImage05,
    BGImage06,
    BGImage07,
    BGImage08,
    BGImage09,
    BGImage10,
    BGImage11
]; 

const blurs = [
    '#E5763E',
    '#915F3B',
    '#604A3C',
    '#838819', //868940ff
    '#4D3A2D',
    '#B5912F',
    '#B56F31',
    '#C98732',
    '#DA7C0B',
    '#C1951E',
    '#B49B33',
    '#C98732'
];

const HomePage: FC = () => {
    const [searchParams] = useSearchParams();
    const { width } = useWindowDimensions();

    const dateNow = new Date();
    const dayOfYear = useMemo(() => {
        return DateTime.now().ordinal;
    }, []);

    const TEN_SECONDS = 10_000;
    const [bgIndex, setBgIndex] = useState<number>(DateTime.now().ordinal % images.length);


    useEffect(() => {
        if (!searchParams.get('galleryMode')) {
            setBgIndex(dayOfYear % images.length);
        }
        else if (searchParams.get('galleryMode')) {
            let interval: NodeJS.Timeout;
    
            const start = () => {
                interval = setInterval(() => {
                    setBgIndex((prev) => (prev + 1) % images.length);
                }, TEN_SECONDS);
            };
            const stop = () => clearInterval(interval);
            document.addEventListener('visibilitychange', () => {
                document.hidden ? stop() : start();
            });
    
            start();
    
            return stop;
        }
    }, []);

    const homePageButtons = useMemo(() => {
        return [
            <HomePageButton
                key={SpeciesTypes.WILDDOG.toString()}
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>WildDog</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.WILDDOG.path}
                img={WildDogIcon}
                imgHover={WildDogHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.DEER.toString()}
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >   
                        <strong>Deer</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.DEER.path}
                img={DeerIcon}
                imgHover={DeerHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.RABBIT.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Rabbit</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.RABBIT.path}
                img={RabbitIcon}
                imgHover={RabbitHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.FERALCAT.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>FeralCat</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALCAT.path}
                img={FeralCatIcon}
                imgHover={FeralCatHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.FOX.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Fox</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.FOX.path}
                img={FoxIcon}
                imgHover={FoxHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.FERALPIG.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>FeralPig</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALPIG.path}
                img={FeralPigIcon}
                imgHover={FeralPigHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.MYNA.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Myna</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.MYNA.path}
                img={MynaIcon}
                imgHover={MynaHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.TOAD.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Toad</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.TOAD.path}
                img={ToadIcon}
                imgHover={ToadHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.MOUSE.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Mouse</strong>Alert
                    </Typography>
                }
                nav={SpeciesTypes.MOUSE.path}
                img={MouseIcon}
                imgHover={MouseHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.FERALGOAT.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>FeralGoat</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALGOAT.path}
                img={FeralGoatIcon}
                imgHover={FeralGoatHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.FERALFISH.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>FeralFish</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALFISH.path}
                img={FeralFishIcon}
                imgHover={FeralFishHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.STARLING.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Starling</strong>Scan
                    </Typography>
                }
                nav={SpeciesTypes.STARLING.path}
                img={StarlingIcon}
                imgHover={StarlingHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.OTHERPESTS.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>Other</strong>Pests
                    </Typography>
                }
                nav={SpeciesTypes.OTHERPESTS.path}
                img={OtherIcon}
                imgHover={OtherHoverIcon}
            />,
            <HomePageButton
                key={SpeciesTypes.NEWPESTS.toString()} 
                label={
                    <Typography 
                        variant="h6"
                        sx={{
                            display: 'inline',
                            whiteSpace: 'nowrap',
                            color: 'white',
                            fontWeight: '200',
                            textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                        }}
                    >
                        <strong>New</strong>Pests
                    </Typography>
                }
                nav={SpeciesTypes.NEWPESTS.path}
                img={NewPestsIcon}
                imgHover={NewPestsHoverIcon}
            />
        ]
    }, []);

    return (
        <Box>
            <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'} height={'100vh'}>
                <Box bgcolor={blurs[bgIndex]}>

                    {/* -- Background Image -- */}
                    <BackgroundImage bgImage={images[bgIndex]} >
                        <BackgroundImageBlurTop />
                        <BackgroundImageBlurBottom bgImageBlur={blurs[bgIndex]} />
                    </BackgroundImage>
                    
                    {/* -- Body -- */}
                    <Box bgcolor={blurs[bgIndex]}>
                        <HomePageHeaderContainer>
                            <HomePageHeader>
                                <Box>
                                    
                                    {
                                        width < 800 ?
                                            <Image 
                                                sx={{
                                                    zIndex: 8,
                                                    display: 'inline',
                                                    height: '103px',
                                                    position: 'relative'
                                                }}
                                                alt="FeralScan Logo" 
                                                src={ FeralScanLogo }
                                            /> :
                                            <Image 
                                                sx={{
                                                    zIndex: 8,
                                                    display: 'inline',
                                                    height: '103px',
                                                    position: 'relative'
                                                }}
                                                alt="FeralScan Logo" 
                                                src={ FeralScanLogo }
                                            />
                                    }
                                </Box>
                                <Box position='relative'>
                                    <HomePageHeaderRightWrapper>
                                        { width >= 800 && (
                                            <Typography fontWeight={500} variant='h3' color='#ff6600'>Providing a community pest animal recording and management tool</Typography>
                                        )}
                                        <Typography paddingTop={'8px'} fontWeight={500} variant='h3' color='white'>Celebrating 15 years of supporting communities, and hosting 1.6 million entries</Typography>
                                    </HomePageHeaderRightWrapper>
                                </Box>
                            </HomePageHeader>
                        </HomePageHeaderContainer>

                        <HomePageTitle>
                                {width < 800 ? (
                                    <MobileHomePageTitleWrapper>
                                        <Typography
                                            sx={{
                                                textAlign: 'center',
                                                fontWeight: '700',
                                                fontSize: '1.5rem',
                                                textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                                            }}
                                        >
                                            Select a species to record pest animal activity in your local area
                                        </Typography>
                                    </MobileHomePageTitleWrapper>
                                ) : (
                                    <HomePageTitleWrapper>
                                        <Typography
                                            variant="h1"
                                            sx={{
                                                textAlign: 'center',
                                                fontWeight: '700',
                                                textShadow: '2px 2px 6px rgba(0, 0, 0, 1)',
                                            }}
                                        >
                                            Record pest animal activity in your local area to protect farms, biodiversity and communities
                                        </Typography>
                                    </HomePageTitleWrapper>
                                )}
                        </HomePageTitle>

                        {width >= 800 && (
                            <>
                                <HomePageBanner>
                                    <HomePageBannerWrapper>
                                        <CommonBanner color="#E5763E" fontWeight='700' text="Select a species to get started"/>
                                    </HomePageBannerWrapper>
                                </HomePageBanner>
                                <HomePageMobileBannerWrapper>
                                    <span>Select a species to get started</span>
                                </HomePageMobileBannerWrapper>
                            </>
                        )}
                
                        <HomePageButtons>
                            <HomePageButtonsWrapper>
                                <HomePageButtonsTable>
                                    {
                                        homePageButtons.map(x => x)
                                    }
                                </HomePageButtonsTable>
                            </HomePageButtonsWrapper>
                        </HomePageButtons>
                    </Box>
                </Box>

                {/* -- Footer -- */}
                <HomePageFooter>
                    <Box display="block" position="relative">
                        <Typography 
                            textAlign="center" 
                            color='black' 
                            fontWeight='bold' 
                            fontSize='0.9rem' 
                            lineHeight='1.6rem'
                            padding='5px 0 20px 0'
                        >
                            Project partners and supporters
                        </Typography>
                        <HomePageFooterSponsors>
                            <HomePageFooterSponsorsWrapper>
                                <HomePageFooterSponsor>
                                    <Link
                                        target="_blank"
                                        href="https://invasives.com.au/"
                                        sx={{
                                            '&:hover': {
                                                opacity: '0.65'
                                            }
                                        }}
                                    >
                                            <Image src={InvasiveSpeciesSolutions}/>
                                    </Link>
                                </HomePageFooterSponsor>
                                <HomePageFooterSponsor>
                                    <Link
                                        target="_blank"
                                        href="https://pestsmart.org.au/"
                                        sx={{
                                            '&:hover': {
                                                opacity: '0.65'
                                            }
                                        }}
                                    >
                                        <Image src={PetSmart}/>
                                    </Link>
                                </HomePageFooterSponsor>
                                <HomePageFooterSponsor>
                                    <Link>
                                        <Image src={DepartmentOfPrimaryIndustries}/>
                                    </Link>
                                </HomePageFooterSponsor>
                                <HomePageFooterSponsor>
                                    <Link>
                                        <Image src={AWI}/>
                                    </Link>
                                </HomePageFooterSponsor>
                                <HomePageFooterSponsor>
                                    <Link>
                                        <Image src={AusGov}/>
                                    </Link>
                                </HomePageFooterSponsor>
                                <HomePageFooterSponsor>
                                    <Link>
                                        <Image src={LocalLandServices}/>
                                    </Link>
                                </HomePageFooterSponsor>
                                <HomePageFooterSponsor>
                                    <Link>
                                        <Image src={Banksia} />
                                    </Link>
                                </HomePageFooterSponsor>
                            </HomePageFooterSponsorsWrapper>
                        </HomePageFooterSponsors>
                    </Box>

                    <Box sx={{ position: 'relative' }}>
                        <Footer sx={{ paddingBottom: '15px'}}>
                            <StyledHr />
                            <List 
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    padding: 0,
                                    margin: '0 auto',
                                    alignItems: 'center',
                                    justifyContent: 'center',

                                    "@media (min-width:800px)": {
                                        width: '100%',
                                        justifyContent: 'space-evenly',
                                        display: 'flex',
                                        padding: 0,
                                        margin: 0,
                                        
                                        "&>*:nth-of-type(2)": {
                                            paddingLeft: '100px'
                                        }
                                    }
                                }}
                            >
                                <StyledListItem>
                                    <List
                                        sx={{
                                            margin: 0,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            textAlign: 'center',

                                            "@media (min-width: 800px)": {
                                                padding: 0,
                                                paddingLeft: '120px',
                                                justifyContent: 'flex-start',
                                            }
                                        }}
                                    >
                                        <ListItem sx={{ margin: "0 0 0 8px", padding: 0 }}>
                                            <StyledHomePageLink href={`/wilddogscan/content/wilddog_contacts`}>
                                                <Typography 
                                                    noWrap 
                                                    whiteSpace="none" 
                                                    marginRight="15px"
                                                    fontWeight="600"
                                                    fontSize="0.75rem"
                                                >
                                                    Contact Us
                                                </Typography>
                                            </StyledHomePageLink>
                                        </ListItem>
                                        <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}><Link><HomePageFooterIcon src={FacebookIcon}/></Link></ListItem>
                                        <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}><Link><HomePageFooterIcon src={TwitterIcon}/></Link></ListItem>
                                        <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}><Link><HomePageFooterIcon src={YoutubeIcon}/></Link></ListItem>
                                        <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}><Link><HomePageFooterIcon src={SubscribeIcon}/></Link></ListItem>
                                    </List>
                                </StyledListItem>

                                <StyledListItem>
                                    <Box>
                                        <StyledHomePageLink href="https://www.pestsmart.org.au/privacy/">
                                            <Typography 
                                                noWrap 
                                                fontSize="0.75rem"
                                                fontWeight="600"
                                                display="inline"
                                            >
                                                Privacy Statement
                                            </Typography>
                                        </StyledHomePageLink>&nbsp;&nbsp;&nbsp;
                                        <Typography fontSize="0.75rem" color="#999" display="inline"> |&nbsp;&nbsp;&nbsp; Copyright © 2009-{dateNow.getFullYear()} Invasive Animals Ltd. Technology by <strong>NewtonGreen</strong></Typography>
                                    </Box>
                                </StyledListItem>
                            </List>
                        </Footer>
                    </Box>
                </HomePageFooter>
            </Box>
        </Box>
    )
}

export default HomePage